import "./bootstrap";
import { Livewire, Alpine } from '../../vendor/livewire/livewire/dist/livewire.esm';
import AlpineRayPlugin from 'alpinejs-ray';
import Tooltip from "@ryangjchandler/alpine-tooltip";
import Clipboard from "@ryangjchandler/alpine-clipboard";
import tippy from "tippy.js";
import PhotoSwipeLightbox from "photoswipe/lightbox";
import PhotoSwipe from "photoswipe";
import "photoswipe/style.css";
import flatpickr from "flatpickr";
import { DateTime } from "luxon";
import Sortable from 'sortablejs';
import Trix from "trix";
import 'trix/dist/trix.css';
import Cleave from 'cleave.js';
window.PhotoSwipeLightbox = PhotoSwipeLightbox;
window.pswpModule = PhotoSwipe;
window.DateTime = DateTime;
window.flatpickr = flatpickr;
window.Sortable = Sortable;
window.Cleave = Cleave;
window.tippy = tippy;

import axios from 'axios';
window.axios = axios;
window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

Alpine.plugin(Clipboard);
Alpine.plugin(Tooltip);

// Register searchSelect Alpine component globally so it's available
// in dynamically rendered Livewire components (e.g. builder modals)
Alpine.data('searchSelect', (config) => ({
    open: false,
    searchText: '',
    focusIndex: null,
    lastSearched: null,
    ...config,

    init() {
        const initialValue = this.$wire.get(this.textProperty);
        this.searchText = initialValue || '';
        this.$wire.$watch(this.textProperty, (v) => {
            if (this.$refs.input !== document.activeElement) {
                this.searchText = v || '';
            }
        });
    },

    belowMinChars(text) {
        return this.minChars > 0 && (text || '').trim().length < this.minChars;
    },

    onFocus() {
        if (this.selected) return;
        this.focusIndex = null;
        // No query on focus with an empty / too-short term (min-chars pickers).
        if (this.belowMinChars(this.searchText)) {
            this.open = this.allowNew && (this.searchText || '').length > 0;
            return;
        }
        this.search(this.searchText);
        this.open = true;
    },

    onInput() {
        const text = this.searchText || '';
        if (this.belowMinChars(text)) {
            // Keep the text in sync but don't search; hide stale results.
            this.$wire.set(this.textProperty, text, false);
            this.lastSearched = null;
            this.open = this.allowNew && text.length > 0;
            this.focusIndex = null;
            return;
        }
        if (text === this.lastSearched) { this.open = true; return; }
        this.search(text);
        this.open = true;
        this.focusIndex = null;
        this.$dispatch(this.name + '-input', text);
    },

    search(text) {
        this.$wire.set(this.textProperty, text, false);
        if (this.focusAction) {
            const target = this.parseFocusAction(this.focusAction);
            this.$wire.$call(target.method, text, ...target.params);
            if (this.loadOnceOnFocus) this.focusAction = null;
        }
        this.lastSearched = text;
    },

    selectResult(index) {
        const results = this.$wire.get(this.resultsProperty);
        const result = results?.[index];
        if (!result) return;
        // Prefer direct property access (arrays / Livewire snapshots) over hasOwnProperty
        // so synthetic or proxied result rows still select correctly.
        const id = (result && typeof result === 'object' && this.idAttr in result)
            ? result[this.idAttr]
            : result;
        const text = (result && typeof result === 'object' && this.textAttr in result)
            ? result[this.textAttr]
            : result;
        this.selected = id;
        this.$wire.set(this.textProperty, text, false);
        this.searchText = text;
        this.$dispatch(this.name + '-selected', id);
        this.$dispatch(this.name + '-selected-object', result);
        this.close();
    },

    selectFocused() {
        if (this.focusIndex === null) return;
        if (this.allowNew && this.searchText?.length > 0 && this.focusIndex === 0) return this.addNew();
        const resultIndex = this.allowNew ? this.focusIndex - 1 : this.focusIndex;
        const results = this.$wire.get(this.resultsProperty);
        if (results && resultIndex >= 0 && resultIndex < results.length) this.selectResult(resultIndex);
    },

    addNew() {
        this.$dispatch(this.name + '-add-new', this.searchText);
        this.close();
    },

    clearSelection() {
        this.selected = null;
        this.$wire.set(this.textProperty, '', false);
        this.searchText = '';
        this.lastSearched = null;
        this.$dispatch(this.name + '-cleared');
        this.$nextTick(() => this.$refs.input?.focus());
    },

    close() { this.open = false; this.focusIndex = null; },

    focusPrev() {
        const results = this.$wire.get(this.resultsProperty);
        if (!results?.length) return;
        if (this.focusIndex === null || this.focusIndex <= 0) { this.focusIndex = null; return; }
        this.focusIndex--;
        this.scrollToFocused();
    },

    focusNext() {
        const results = this.$wire.get(this.resultsProperty);
        if (!results?.length) return;
        const total = this.totalResults();
        if (this.focusIndex === null) this.focusIndex = 0;
        else if (this.focusIndex < total - 1) this.focusIndex++;
        this.scrollToFocused();
    },

    totalResults() {
        const results = this.$wire.get(this.resultsProperty);
        let count = results ? results.length : 0;
        if (this.allowNew && this.searchText?.length > 0) count++;
        return count;
    },

    scrollToFocused() {
        if (this.focusIndex === null) return;
        this.$nextTick(() => {
            const el = this.$refs['result-' + this.focusIndex];
            el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
    },

    parseFocusAction(raw) {
        let method = raw; let params = [];
        const match = raw.match(/(.*?)\((.*)\)/);
        if (match) { method = match[1]; try { params = JSON.parse('[' + match[2] + ']'); } catch (e) { params = []; } }
        return { method, params };
    },
}));

window.Alpine = Alpine;
Alpine.plugin(AlpineRayPlugin);

Livewire.start();
